import { Component, Inject, ViewChild } from '@angular/core';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject, takeUntil } from 'rxjs';
import { AlertService } from '../../../_services/alert.service';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  NgForm,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { JsEmail } from '../../../_interfaces/email.interface';
import { getNewId } from '../../../_utils/utils';
import _ from 'lodash';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { SelectComponent } from '../../../shared/select/select.component';
import { MatTooltip } from '@angular/material/tooltip';
import { FirebaseOptimisticService } from '../../../_services/firebase-optimistic.service';
import { defaultEmailTemplate } from '../../../_datas/const.data';
import { Router, RouterModule } from '@angular/router';
import { NoSpacesDirective } from '../../../_directives/no-space.directive';
import { languagesWithDisplayName } from '../../../_datas/languages.data';
import { EmailService } from '../../../_services/email.service';
import { FirestoreStoreService } from '../../../_services/firestore-store.service';

@Component({
  selector: 'app-email-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatTooltip,
    SelectComponent,
    CdkTextareaAutosize,
    NoSpacesDirective,
    RouterModule,
  ],
  templateUrl: './email-form.component.html',
  styleUrl: './email-form.component.scss',
})
export class EmailFormComponent {
  @ViewChild('emForm') emForm!: NgForm;
  unSubscribe = new Subject<void>();
  emailForm: FormGroup;
  emailInitial: JsEmail;
  mode: 'new' | 'edit';
  languages = languagesWithDisplayName;

  constructor(
    public dialogRef: MatDialogRef<EmailFormComponent>,
    private alertService: AlertService,
    @Inject(MAT_DIALOG_DATA)
    public data: { mode: 'new' | 'edit'; value: JsEmail; hideEditButton?: boolean },
    private fb: FormBuilder,
    private fbo: FirebaseOptimisticService,
    private router: Router,
    private es: EmailService,
    private store: FirestoreStoreService
  ) {
    this.mode = data.mode;
    this.emailInitial =
      data.mode == 'new' ? this.initiateForm() : this.initiateForm(data.value);

    console.log('emailInitial--->>', this.emailInitial);

    this.emailForm = this.fb.group({
      id: new FormControl(this.emailInitial.id, Validators.required),
      version: new FormControl(this.emailInitial.version, Validators.required),
      description: new FormControl(
        this.emailInitial.description,
        Validators.required
      ),
      htmlPart: new FormControl(
        this.emailInitial.htmlPart,
        Validators.required
      ),
      subjectPart: new FormControl(this.emailInitial.subjectPart),
      templateVariables: new FormControl(this.emailInitial.templateVariables),
      defaultLangCode: new FormControl(
        this.emailInitial.defaultLangCode,
        Validators.required
      ),
      templateName: new FormControl(this.emailInitial.templateName, [
        Validators.required,
        Validators.max(50),
        Validators.maxLength(50),
      ]),
      publishedSnapshot: new FormControl(this.emailInitial.publishedSnapshot || null),
      contentHash: new FormControl(this.emailInitial.contentHash || ''),
    });

    // Sync htmlPart and subjectPart keys when defaultLangCode changes
    // This ensures the selected language key exists in both content objects
    this.emailForm.get('defaultLangCode')?.valueChanges
      .pipe(takeUntil(this.unSubscribe))
      .subscribe((newLangCode: string) => {
        if (newLangCode && this.mode === 'new') {
          const currentHtmlPart = this.emailForm.get('htmlPart')?.value || {};
          const currentSubjectPart = this.emailForm.get('subjectPart')?.value || {};

          // If the new language key doesn't exist, add it with content from 'en' or default
          if (!(newLangCode in currentHtmlPart)) {
            this.emailForm.patchValue({
              htmlPart: {
                ...currentHtmlPart,
                [newLangCode]: currentHtmlPart['en'] || defaultEmailTemplate
              }
            });
          }
          if (!(newLangCode in currentSubjectPart)) {
            this.emailForm.patchValue({
              subjectPart: {
                ...currentSubjectPart,
                [newLangCode]: currentSubjectPart['en'] || ''
              }
            });
          }
        }
      });

    dialogRef
      .backdropClick()
      .pipe(takeUntil(this.unSubscribe))
      .subscribe(async () => {
        if (this.hasChanges()) {
          const confirmed = await this.alertService.confirm(
            'Alert',
            'You have unsaved changes. Do you really want to discard them?',
            'Discard',
            'Cancel'
          );
          if (!confirmed) {
            return;
          }
          this.dialogRef.close();
        } else {
          this.dialogRef.close();
        }
      });
  }

  initiateForm(email?: JsEmail): JsEmail {
    return {
      id: email ? email.id : getNewId(),
      version: email ? email.version : 1,
      description: email ? email.description : '',
      htmlPart: email ? email.htmlPart : { en: defaultEmailTemplate },
      subjectPart: email ? email.subjectPart : { en: '' },
      templateVariables: email ? email.templateVariables : [],
      defaultLangCode: email ? email.defaultLangCode : 'en',
      templateName: email ? email.templateName : '',
      publishedSnapshot: email ? email.publishedSnapshot : null,
      contentHash: email ? email.contentHash : '',
    };
  }

  getFc(fcName: string): FormControl {
    return this.emailForm.get(fcName) as FormControl;
  }

  hasChanges() {
    const initial = _.cloneDeep(this.emailInitial);
    const current = _.cloneDeep(this.emailForm.value);
    return !_.isEqual(initial, current);
  }

  async save() {
    const emailBackup = _.cloneDeep(this.emailForm.value);
    let updateStatus = null;

    const updateId = this.emailForm.value.id;
    if (!this.emailForm.value.id) return;
    if (this.mode === 'new') {
      const templateName = `${this.emailForm.value.templateName}-${this.emailForm.value.defaultLangCode}`;
      const existingTemplate = this.es
        .emailTemplates()
        .find((t) => t.Name === templateName);
      if (existingTemplate) {
        this.alertService.confirm(
          'Template Name Exists',
          'A template with the same name already exists. Please choose a different name.',
          'OK',
          'Cancel',
          false
        );
        return;
      } else {
        // Create email via Firebase optimistic service (handles Firestore + IndexedDB)
        updateStatus = await this.fbo.createItemsOptimistic<JsEmail>(
          [this.emailForm.value],
          'emails'
        );
        if (updateStatus && updateId === this.emailInitial.id) {
          this.mode = 'edit';
          this.emailInitial = _.cloneDeep(emailBackup);

          // Build the complete email object with all required fields for FirestoreStore
          // This matches what createItemsOptimistic saves to Firestore
          const now = new Date();
          const uid = JSON.parse(localStorage.getItem('user')!)?.uid;
          const completeEmail: JsEmail = {
            ...this.emailForm.value,
            uid: uid,
            createdBy: uid,
            updatedBy: uid,
            createdAt: now,
            updatedAt: now,
          };

          // Add to FirestoreStore so email-editor can find it immediately
          this.store.addEmailOptimistic(completeEmail);

          this.reset();
          this.router.navigate(['email-editor', this.emailForm.value.id]);
        }
      }
    } else if (this.mode === 'edit') {
      updateStatus = await this.fbo.updateItemsOptimistic<JsEmail>(
        [this.emailForm.value],
        'emails'
      );
      this.emailInitial = _.cloneDeep(this.emailForm.value);

      // Build complete email for update
      const now = new Date();
      const uid = JSON.parse(localStorage.getItem('user')!)?.uid;
      const completeEmail: JsEmail = {
        ...this.emailForm.value,
        uid: uid,
        updatedBy: uid,
        updatedAt: now,
      };
      this.store.updateEmailOptimistic(completeEmail);
    }

    if (!updateStatus) {
      this.emailInitial = _.cloneDeep(emailBackup);
    }
    // Return the saved email data when closing
    this.dialogRef.close(updateStatus ? this.emailForm.value : undefined);
  }

  async deleteHandler(isDelete: boolean) {
    if (this.emailInitial.publishedSnapshot) {
      this.alertService.confirm(
        'Cannot Delete Published Email',
        'You cannot delete a published email template. Please unpublish it first.',
        'OK',
        'Cancel',
        false
      );
      return;
    } else {
      const confirmed = await this.alertService.confirm(
        `${isDelete ? 'Delete' : 'Restore'} Email`,
        `Are you sure you want to ${
          isDelete ? 'delete' : 'restore'
        } this email?`
      );
      if (confirmed) {
        if (!this.emailForm.value.id || this.mode === 'new') return;
        this.emailInitial.deletedAt = isDelete ? new Date() : null;
        await this.fbo.updateItemsOptimistic<JsEmail>(
          [this.emailInitial],
          'emails'
        );
        this.emailInitial = this.initiateForm();
        this.reset();
        this.dialogRef.close();
      }
    }
  }

  closeDialog() {
    this.dialogRef.close();
  }

  reset() {
    this.emailForm.reset();
    this.emForm.resetForm();
    this.emailForm.patchValue(this.emailInitial);
  }
}
